import React, { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import InstallMobileRoundedIcon from '@mui/icons-material/InstallMobileRounded';
import SectionCard from './ui/SectionCard';
import InstallAppDialog from './InstallAppDialog';
import { useAuth } from '../AuthContext';
import api from '../api';
import { biometriaDisponivel, cadastrarBiometria, marcarLocal } from '../utils/biometria';
import { assinar, podeInstalar, rodandoComoApp } from '../utils/pwa';

// Meu perfil › Acesso: aparelhos com biometria e instalação do app.
const BiometriaCard = ({ sx, onMessage }) => {
  const { userLogado } = useAuth();
  const [credenciais, setCredenciais] = useState([]);
  const [disponivel, setDisponivel] = useState(false);
  const [ativando, setAtivando] = useState(false);
  const [instalavel, setInstalavel] = useState(podeInstalar());
  const [instalarAberto, setInstalarAberto] = useState(false);

  const carregar = () => api.get('webauthn/credenciais').then((r) => setCredenciais(r.data.credenciais)).catch(() => {});

  useEffect(() => {
    carregar();
    biometriaDisponivel().then(setDisponivel);
    return assinar(() => setInstalavel(podeInstalar()));
  }, []);

  const ativar = async () => {
    setAtivando(true);
    const resultado = await cadastrarBiometria(userLogado._id);
    setAtivando(false);
    onMessage && onMessage(resultado.ok ? 'Biometria ativada neste aparelho.' : resultado.message, resultado.ok ? 'success' : 'error');
    carregar();
  };

  const remover = async (id) => {
    await api.remove(`webauthn/credenciais/${id}`);
    marcarLocal(userLogado._id, 'recusada');
    onMessage && onMessage('Aparelho removido.');
    carregar();
  };

  return (
    <SectionCard title="Acesso rápido" subtitle="Biometria e app no celular" icon={<FingerprintRoundedIcon color="primary" />} sx={sx}>
      <Stack spacing={2}>
        {credenciais.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nenhum aparelho com biometria cadastrada.</Typography>
        ) : (
          <Stack spacing={1}>
            {credenciais.map((c) => (
              <Stack key={c._id} direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.25, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <PhoneIphoneRoundedIcon color="action" />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{c.nome || 'Aparelho'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Cadastrado em {new Date(c.createdAt).toLocaleDateString('pt-BR')}
                    {c.lastUsedAt ? ` · último uso ${new Date(c.lastUsedAt).toLocaleDateString('pt-BR')}` : ''}
                  </Typography>
                </Box>
                <Tooltip title="Remover este aparelho">
                  <IconButton size="small" color="error" onClick={() => remover(c._id)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}
        {disponivel ? (
          <Button variant="outlined" startIcon={<FingerprintRoundedIcon />} onClick={ativar} disabled={ativando}>Ativar biometria neste aparelho</Button>
        ) : (
          <Alert severity="info" variant="outlined">Este aparelho/navegador não oferece biometria. Abra pelo celular (Safari no iPhone ou Chrome no Android).</Alert>
        )}
        {!rodandoComoApp() && instalavel && (
          <Button variant="contained" startIcon={<InstallMobileRoundedIcon />} onClick={() => setInstalarAberto(true)}>Instalar o app no celular</Button>
        )}
      </Stack>
      <InstallAppDialog open={instalarAberto} onClose={() => setInstalarAberto(false)} onRecusar={() => setInstalarAberto(false)} />
    </SectionCard>
  );
};

export default BiometriaCard;
